import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '@sentinel/api-client';
import { StepUpDialog, type StepUpMessages } from './dialog';
import { getStepUpQueue, type StepUpQueue } from './queue';

/** Detail dispatched on the `sentinel:step-up-required` window CustomEvent. */
export interface StepUpRequiredEventDetail {
  /** Queue entry id. The middleware awaits the queue entry with this id. */
  id: string;
  /** ACR value the backend says is required. */
  acr: string;
}

declare global {
  interface WindowEventMap {
    'sentinel:step-up-required': CustomEvent<StepUpRequiredEventDetail>;
  }
}

export interface StepUpProviderProps {
  apiClient: ApiClient;
  messages: StepUpMessages;
  /** Show the optional TOTP input. Default: false. */
  showTotp?: boolean;
  /** Optional override; defaults to the shared singleton queue. */
  queue?: StepUpQueue;
  children?: ReactNode;
}

interface ActiveRequest {
  id: string;
  acr: string;
}

/**
 * Listens for `sentinel:step-up-required` window events fired by the
 * api-client middleware. When fired, mounts the StepUpDialog. The dialog
 * resolves/rejects the matching queue entry, which lets the middleware retry
 * (or abort) the original request.
 */
export function StepUpProvider({
  apiClient,
  messages,
  showTotp = false,
  queue,
  children,
}: StepUpProviderProps) {
  const q = queue ?? getStepUpQueue();
  const [active, setActive] = useState<ActiveRequest | null>(null);
  // Keep `q` stable across renders for the listener's closure.
  const queueRef = useRef(q);
  queueRef.current = q;

  useEffect(() => {
    function onStepUp(e: WindowEventMap['sentinel:step-up-required']) {
      const detail = e.detail;
      if (!detail || typeof detail.id !== 'string') return;
      // If a dialog is already open, the new entry queues up; show it once
      // the active one finishes by switching `active` after resolution.
      setActive((current) => current ?? { id: detail.id, acr: detail.acr });
    }
    window.addEventListener('sentinel:step-up-required', onStepUp);
    return () => window.removeEventListener('sentinel:step-up-required', onStepUp);
  }, []);

  const advance = useCallback(() => {
    // After resolving/rejecting the active entry, peek at the next pending
    // queue entry (if the middleware enqueued more while we were busy) and
    // open another dialog for it. We read the per-entry payload so the next
    // dialog uses the correct ACR — never reuse the previous active.acr,
    // which would be racy across step-ups for different ACRs.
    const next = queueRef.current.peek();
    if (next) {
      const payload = queueRef.current.getPayload(next);
      if (payload) {
        setActive({ id: next, acr: payload.acr });
        return;
      }
      // Payload missing (shouldn't happen): close, and rely on the window
      // event for the next entry to reopen the dialog.
    }
    setActive(null);
  }, []);

  const handleSuccess = useCallback(() => {
    if (!active) return;
    queueRef.current.resolve(active.id);
    advance();
  }, [active, advance]);

  const handleCancel = useCallback(() => {
    if (!active) return;
    queueRef.current.reject(active.id, new Error('step_up_cancelled'));
    advance();
  }, [active, advance]);

  return (
    <>
      {children}
      <StepUpDialog
        open={active !== null}
        apiClient={apiClient}
        messages={messages}
        targetAcr={active?.acr ?? ''}
        showTotp={showTotp}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </>
  );
}
